import { operationIds } from "@gen/app-access-config";
import { CoreQuery, type IClientResponse } from "@gen/app-query";
import { type AnyRoute, createRoute, redirect } from "@tanstack/react-router";

import { Client } from "./Client";
import { CreateClient } from "./CreateClient";

const CLIENT_SETTINGS_DEFAULT_TITLE = "Client Settings";

export function createClientRoute<P extends AnyRoute>(parent: P) {
  return createRoute({
    getParentRoute: () => parent,
    path: "admin/clients/$clientId",
    component: Client,
    staticData: { reducedMobileTopPadding: true },
    loader: ({ params, context }) => {
      const queryClient = context.queryClient;
      const queryKey = CoreQuery.admin_getClientQueryKey(params.clientId);

      // `context` is untyped here because the route factory takes an AnyRoute
      // parent, which erases the router's AuthCtx context; cast the result.
      const cachedClient = queryClient.getQueryData(queryKey) as
        | IClientResponse
        | undefined;

      const calculateTitle = (prefix: string, name: string) => {
        return prefix + " " + name;
      };

      let titlePromise = queryClient
        .fetchQuery({
          queryKey,
          queryFn: () => CoreQuery.Client().admin_getClient(params.clientId),
        })
        .then((r: IClientResponse) =>
          calculateTitle(CLIENT_SETTINGS_DEFAULT_TITLE, r.name),
        );

      if (cachedClient) {
        titlePromise = Promise.resolve(
          calculateTitle(CLIENT_SETTINGS_DEFAULT_TITLE, cachedClient.name),
        );
      } else {
        queryClient.prefetchQuery({
          queryKey,
          queryFn: () => CoreQuery.Client().admin_getClient(params.clientId),
        });
      }

      return {
        titlePromise,
      };
    },
    head: () => ({
      meta: [
        {
          title: CLIENT_SETTINGS_DEFAULT_TITLE,
        },
      ],
    }),
  });
}

export function createCreateClientRoute<P extends AnyRoute>(parent: P) {
  return createRoute({
    getParentRoute: () => parent,
    path: "admin/clients/create",
    component: CreateClient,
    beforeLoad: (opts) => {
      const canCreateClient = opts.context.canExecuteAdminOperation(
        operationIds["core_admin.createClientWithId"],
      );

      if (!canCreateClient) {
        throw redirect({
          to: "/admin/clients",
          replace: true,
        });
      }
    },
    head: () => ({
      meta: [
        {
          title: "Create Client",
        },
      ],
    }),
  });
}
